import { Button } from '@components'
import { useBusinessContext } from '@context'
import {
  AccrualChoice,
  AccountingMethodSelector,
  getRecommendedAccrualChoice
} from '@containers/GetStartedWidget/AccountingMethodSelector'
import { DocsLink, HeaderTile } from '@containers/GetStartedWidget/AccrualAccountingModal.styled'
import { updateBusiness } from '@query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaExternalLinkAlt, FaFileInvoice } from 'react-icons/fa'
import styled from 'styled-components'

export const AccountingMethodTab: React.FC = () => {
  const [t] = useTranslation()
  const { slug, data: business } = useBusinessContext()
  const queryClient = useQueryClient()

  const savedChoice: AccrualChoice = business?.has_accrual_based_entries_for_invoicing
    ? 'yes'
    : 'no'
  const [selected, setSelected] = useState<AccrualChoice>(savedChoice)
  const [prevSaved, setPrevSaved] = useState(savedChoice)

  if (prevSaved !== savedChoice) {
    setPrevSaved(savedChoice)
    setSelected(savedChoice)
  }

  const isDirty = selected !== savedChoice
  const recommended = getRecommendedAccrualChoice(business?.form)
  const methodLabel =
    savedChoice === 'yes'
      ? t('dashboard.getStartedWidget.accrualAccounting.yes.title')
      : t('dashboard.getStartedWidget.accrualAccounting.no.title')

  const saveMutation = useMutation({
    mutationFn: (choice: AccrualChoice) =>
      updateBusiness({ slug }, { has_accrual_based_entries_for_invoicing: choice === 'yes' }),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [slug] })
      toast.success(t('general.changesSaved'))
    },
    onError: () => toast.error(t('general.error'))
  })

  return (
    <MethodCard>
      <CardBody>
        <StatusRow>
          <HeaderTile>
            <FaFileInvoice />
          </HeaderTile>
          <StatusText>
            <StatusTitle>{t('workflows.hub.methodTitle', { method: methodLabel })}</StatusTitle>
          </StatusText>
        </StatusRow>

        <AccountingMethodSelector
          value={selected}
          onChange={setSelected}
          businessForm={business?.form}
          recommended={recommended}
        />
      </CardBody>

      <CardFooter>
        <DocsLink
          href={t('link.docs.accrualBasedInvoicing')}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('dashboard.getStartedWidget.accrualAccounting.readMore')}
          <FaExternalLinkAlt />
        </DocsLink>

        {isDirty && (
          <FooterActions>
            <ChangeNote>{t('workflows.hub.methodChangeNote')}</ChangeNote>
            <Button
              type="button"
              isSecondary
              onClick={() => setSelected(savedChoice)}
              disabled={saveMutation.isPending}
            >
              {t('general.cancel')}
            </Button>
            <Button
              type="button"
              intent="primary"
              showSpinner={saveMutation.isPending}
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate(selected)}
            >
              {t('general.save')}
            </Button>
          </FooterActions>
        )}
      </CardFooter>
    </MethodCard>
  )
}

const MethodCard = styled.div`
  max-width: 720px;
  width: 100%;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  overflow: hidden;
`

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;
`

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const StatusText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`

const StatusTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.lg}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const ChangeNote = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.dashboard.textSubtle};
`
